import React from 'react';
import {
    Box,
    Card,
    Chip,
    Stack,
    Typography,
    useTokens,
} from '@barracuda-internal/bds-core';
import {
    CATEGORY_LABEL,
    CATEGORY_TO_SCANNER,
    CATEGORY_DESCRIPTION,
} from '../findings/categoryToScanner';

// ---------------------------------------------------------------------------
// Severity → BDS chip type mapping
// ---------------------------------------------------------------------------

const SEVERITY_BDSTYPE = {
    critical: 'neutralError',
    high:     'errorSubtle',
    medium:   'warningSubtle',
    low:      'staticNeutral',
};

// ---------------------------------------------------------------------------
// Per-finding card (used when scan.findings[] is non-empty)
// ---------------------------------------------------------------------------

function FindingCard({ finding }) {
    const tokens = useTokens();
    return (
        <Card bdsType="staticSubtle" bdsPadding="default" data-testid="finding-card">
            <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                {/* Type chip + severity chip + source */}
                <Stack
                    direction="row"
                    sx={{ gap: tokens.aliasesSpaceComponentDense, alignItems: 'center' }}
                >
                    <Chip bdsType="staticNeutral" bdsSize="medium" label={finding.type} />
                    {finding.severity && (
                        <Chip
                            bdsType={SEVERITY_BDSTYPE[finding.severity] || 'staticNeutral'}
                            bdsSize="small"
                            label={finding.severity}
                        />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="captionSubtle">{finding.source}</Typography>
                </Stack>

                {/* Category + confidence */}
                <Stack
                    direction="row"
                    sx={{ gap: tokens.aliasesSpaceComponentDefault, alignItems: 'center' }}
                >
                    <Typography variant="captionNeutral">
                        {CATEGORY_LABEL[finding.category] || finding.category}
                    </Typography>
                    {finding.confidence != null && (
                        <Typography variant="captionSubtle">
                            Confidence: {Math.round(finding.confidence * 100)}%
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FindingsTab({ scan }) {
    const tokens = useTokens();
    const findings = scan.findings || [];
    const cats = scan.finding_categories || [];

    // Rich per-finding cards when findings[] has data
    if (findings.length > 0) {
        return (
            <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                {findings.map((f, i) => (
                    <FindingCard key={i} finding={f} />
                ))}
            </Stack>
        );
    }

    // Empty state
    if (cats.length === 0) {
        return (
            <Typography variant="body1" sx={{ opacity: 0.6 }}>
                No findings for this scan.
            </Typography>
        );
    }

    // Legacy fallback: category-based rendering (Phase 1 behaviour)
    return (
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
            {/* Header count line */}
            <Typography variant="captionSubtle">
                {cats.length} finding{' '}
                {cats.length === 1 ? 'category' : 'categories'} detected
            </Typography>

            {/* One card per finding category */}
            {cats.map((c) => (
                <Card
                    key={c}
                    data-testid="finding-card"
                    bdsType="staticSubtle"
                    bdsPadding="default"
                >
                    <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                        {/* Row: chip + scanner name */}
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Chip
                                bdsType="staticNeutral"
                                label={CATEGORY_LABEL[c] || c}
                            />
                            <Typography variant="captionSubtle">
                                {CATEGORY_TO_SCANNER[c] || 'unknown'}
                            </Typography>
                        </Stack>

                        {/* Description */}
                        <Typography variant="body1">
                            {CATEGORY_DESCRIPTION[c] || ''}
                        </Typography>

                        {/* Phase 2: classifier, confidence, redacted evidence */}
                    </Stack>
                </Card>
            ))}
        </Stack>
    );
}
